import * as React from 'react';
import type {RequestIframe} from '../iframe-overrides/types';

type Report = (
  error: Error,
  scope?: {
    tags?: Record<string, string>;
    extras?: Record<string, unknown>;
  },
) => void;

export type UiConfig = Readonly<{
  // We cannot import '@sail/observability' from '@sail/ui' since
  // '@sail/observability' is not versioned so we have to inject it externally.
  _internal_observability?: Readonly<{
    reports?: Readonly<{
      error: Report;
      warning: Report;
    }>;
    analytics?: Readonly<{
      track: (eventName: string, parameters?: Record<string, unknown>) => void;
    }>;
  }>;

  // The bottom sheet feature is disabled by default while it's being tested.
  // Can be a boolean to enable/disable for all overlay types, or an object to
  // enable/disable per overlay type (dialog, drawer, popover).
  enableBottomSheets?:
    | boolean
    | {
        dialog?: boolean;
        drawer?: boolean;
        popover?: boolean;
      };
  // Enable the form management integration in Form components
  enableFormManagement?: boolean;
  /**
   * Disable the react aria focus ring. Use the native css focus selector instead.
   */
  disableReactAriaFocusRing?: boolean;
  // Disable the default react aria routing
  disableDefaultReactAriaRouting?: boolean;
  /**
   * Disable the swipe down gesture for bottom sheets.
   */
  disableBottomSheetsSwipeDown?: boolean;

  // Enable a persistent hidden submit button in Button when behavior="submit",
  // allowing implicit form submission (Enter key) with multiple inputs.
  enableImplicitFormSubmission?: boolean;

  // When enabled, removes the setTimeout workaround in Button onPress for
  // mobile devices in favor of calling the callback directly.
  enableButtonRemoveSetTimeout?: boolean;

  // When enabled, renders the new experimental CodePuncher (unstable_CodePuncher)
  // which includes animations and enhanced visual feedback.
  enableRefreshedCodePuncher?: boolean;

  // Configuration options to apply iframe overrides
  _unstable_getUILayers?: () => Window[];
  _unstable_requestIframe?: RequestIframe;
  _unstable_skipFocusContainment?: boolean;
}>;

export type PartialUiConfig = Partial<UiConfig>;

const UiConfigContext = React.createContext<UiConfig | null>(null);

export function useUiConfig(): UiConfig | null {
  return React.useContext(UiConfigContext);
}

export const UiConfigProvider: React.FC<
  React.PropsWithChildren<{config: UiConfig}>
> = ({config, children}) => {
  const parentConfig = useUiConfig();
  const mergedConfig = React.useMemo(
    () => ({
      ...(parentConfig ?? {}),
      ...config,
    }),
    [parentConfig, config],
  );

  return (
    <UiConfigContext.Provider value={mergedConfig}>
      {children}
    </UiConfigContext.Provider>
  );
};
